import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
  useEffect,
  useRef,
} from 'react';

/** Shared mobile-first primitives: one style source for every screen. */

const buttonBase =
  'inline-flex min-h-11 items-center justify-center rounded-xl px-5 py-2.5 text-base font-medium transition active:scale-[0.98] disabled:opacity-50';

const buttonVariants = {
  primary: 'bg-primary-600 text-white hover:bg-primary-700',
  secondary:
    'border border-divider bg-surface-primary text-content-primary hover:bg-interactive-hover',
  danger: 'bg-red-600 text-white hover:bg-red-700',
} as const;

export function Button({
  variant = 'primary',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: keyof typeof buttonVariants }) {
  return (
    <button
      type="button"
      className={`${buttonBase} ${buttonVariants[variant]} ${className}`}
      {...rest}
    />
  );
}

export function TextInput({
  ref,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return (
    <input
      ref={ref}
      className="min-h-11 w-full rounded-xl border border-divider bg-surface-primary px-4 py-2.5 text-base text-content-primary placeholder:text-content-tertiary focus:border-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      {...props}
    />
  );
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className="w-full rounded-xl border border-divider bg-surface-primary px-4 py-2.5 text-base text-content-primary placeholder:text-content-tertiary focus:border-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      {...props}
    />
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="block">
      <span className="mb-1 block text-sm font-medium text-content-secondary">{label}</span>
      {children}
    </div>
  );
}

export function Card({ children, label }: { children: ReactNode; label?: string }) {
  if (!label) {
    return (
      <div className="rounded-2xl border border-divider bg-surface-elevated p-4 shadow-sm">
        {children}
      </div>
    );
  }
  return (
    <section
      aria-label={label}
      className="rounded-2xl border border-divider bg-surface-elevated p-4 shadow-sm"
    >
      {children}
    </section>
  );
}

const pageClass =
  'mx-auto w-full max-w-md space-y-4 px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4';

export function Page({ children, label }: { children: ReactNode; label?: string }) {
  if (!label) {
    return <div className={pageClass}>{children}</div>;
  }
  return (
    <section aria-label={label} className={pageClass}>
      {children}
    </section>
  );
}

export function Alert({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-700">
      {children}
    </p>
  );
}

// Open modals, oldest first. Every modal listens on document, so only the top one may act on
// Escape; without this, closing a viewer stacked on the camera would also close the camera.
const modalStack: { root: Element | null }[] = [];
// Body overflow from before the first modal opened; restored when the last one closes, in
// whatever order they unmount.
let pageOverflow = '';
// Only what this module set, so closing a modal never clears an `inert` someone else owns.
let inerted: Element[] = [];

/**
 * Makes everything outside the top modal inert. Modals render in place, not in a portal, so
 * this marks the siblings of each ancestor of the dialog up to `<body>`.
 */
function isolateTopModal() {
  for (const el of inerted) el.removeAttribute('inert');
  inerted = [];
  let node = modalStack.at(-1)?.root;
  while (node?.parentElement && node !== document.body) {
    for (const sibling of node.parentElement.children) {
      if (sibling !== node && !sibling.hasAttribute('inert')) {
        sibling.setAttribute('inert', '');
        inerted.push(sibling);
      }
    }
    node = node.parentElement;
  }
}

/**
 * Modal behavior: focuses `closeRef`, closes on Escape when it is the top modal and no field
 * inside already handled the key (`preventDefault`), makes the page outside the top modal's
 * `role="dialog"` ancestor inert, locks page scroll until the last modal closes, and on
 * unmount restores the focused opener it found.
 */
export function useModal(onClose: () => void) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const token = { root: closeRef.current?.closest('[role="dialog"]') ?? null };
    modalStack.push(token);
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    isolateTopModal();
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        !e.defaultPrevented &&
        modalStack[modalStack.length - 1] === token
      ) {
        onCloseRef.current();
      }
    };
    if (modalStack.length === 1) pageOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      modalStack.splice(modalStack.indexOf(token), 1);
      if (modalStack.length === 0) document.body.style.overflow = pageOverflow;
      // Before refocusing: an inert opener can't take focus.
      isolateTopModal();
      opener?.focus();
    };
  }, []);
  return closeRef;
}

/** Calls `onEscape` on Escape while no modal is open; an open modal takes the key. */
export function useEscape(onEscape: () => void) {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && modalStack.length === 0) {
        onEscapeRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}

/** Modal side panel over the whole app. */
export function Drawer({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const closeRef = useModal(onClose);

  // The dialog includes the backdrop: useModal makes the dialog's siblings inert, and an
  // inert backdrop would ignore the click that closes the drawer.
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-30">
      <div aria-hidden="true" className="absolute inset-0 bg-gray-900/50" onClick={onClose} />
      <section className="absolute inset-y-0 right-0 flex w-80 max-w-[85vw] flex-col bg-surface-primary shadow-xl">
        <div className="flex items-center justify-between border-b border-divider px-4 py-2.5">
          <span className="text-base font-bold text-content-primary">{title}</span>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg text-lg text-content-secondary hover:bg-interactive-hover"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
        <div className="space-y-2 overflow-y-auto px-4 py-3">{children}</div>
      </section>
    </div>
  );
}

export function FloppyIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V7l-4-4zM17 3v5H7V3m10 13v-5H7v5"
      />
    </svg>
  );
}

export function TrashIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" />
    </svg>
  );
}

export function ShareIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM19 8v6M22 11h-6" />
    </svg>
  );
}

/** The app mark from `website/rosa-icon.svg`: a house with a magnifier. */
export function RosaIcon() {
  return (
    <svg aria-hidden="true" className="h-5 w-5 shrink-0" viewBox="0 0 64 64">
      <rect x="4" y="28" width="56" height="32" rx="4" fill="#7c3aed" />
      <path d="M4 30 L32 6 L60 30 Z" fill="#8b5cf6" />
      <rect x="26" y="42" width="12" height="18" rx="1" fill="#ffffff" />
      <circle cx="46" cy="14" r="7" fill="none" stroke="#111827" strokeWidth={3} />
      <line
        x1="51"
        y1="19"
        x2="58"
        y2="26"
        stroke="#111827"
        strokeWidth={3}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return <p className="text-sm text-content-secondary">{children}</p>;
}

export function ViewOnlyNote() {
  return <Muted>View only. You can look through this project but not change it.</Muted>;
}
